import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { createPortal } from 'react-dom';
import { Home, Package, Banknote, FileText, User, LifeBuoy, Route } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { shipmentServiceReal } from '../services/shipmentServiceReal';
import { notificationService } from '../services/notificationService';
import { driverService } from '../services/driverService';
import { getStoredDriverMobileUser } from '../utils/driverMobileSession';
import { announceNewNotifications, primeNotificationAlertCache } from '../utils/inAppNotificationAlert';
import { useAuth } from '../contexts/AuthContext';
import { isCorporateExternalTransportMobileUser } from '../utils/corporateMobileUser';
import { CorporateQuickActions } from './CorporateQuickActions';
import { alwaysLocationService } from '../services/alwaysLocationService';
import { API_CONFIG, getAuthToken } from '../config/api';
import { isInternalDriverUser } from '../utils/userRoles';

export function DriverBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser } = useAuth();
  const locationWatchRef = useRef<number | null>(null);
  const lastLocationSentRef = useRef<number>(0);
  const notificationCachePrimedRef = useRef(false);

  useEffect(() => {
    const user = getStoredDriverMobileUser<any>() || authUser;
    if (!user || !navigator.geolocation) return;
    if (isInternalDriverUser(user)) return;
    if (isCorporateExternalTransportMobileUser(user)) return;
  const isDriver = user?.role === 'external-driver' || user?.role === 'corporate-external-transport-driver' || user?.role === 'corporate-external-transport-user' || user?.role === 'driver' || user?.driverType === 'external';
    if (!isDriver) return;

    const syncServerLocation = async () => {
      try {
        const response: any = await driverService.getDriverByUserId(user.id);
        const driver = response?.data || response;
        if (!driver?.latest_location_at) return;

        localStorage.setItem('driverLocation', JSON.stringify({
          latitude: Number(driver.latest_location_latitude),
          longitude: Number(driver.latest_location_longitude),
          timestamp: new Date(driver.latest_location_at).toISOString(),
          source: 'server',
        }));
        window.dispatchEvent(new CustomEvent('driverLocationUpdate'));
      } catch (error) {
        console.error('Failed to load latest driver location:', error);
      }
    };

    void syncServerLocation();

    const startWatch = async () => {
      if (Capacitor.isNativePlatform()) {
        const authToken = getAuthToken();
        if (authToken) {
          await alwaysLocationService.configureTrackingIfAvailable({
            apiBaseUrl: API_CONFIG.BASE_URL,
            authToken,
          });
        }
        await alwaysLocationService.requestAlwaysIfAvailable();
        await alwaysLocationService.startBackgroundTrackingIfAvailable();
      }

      locationWatchRef.current = navigator.geolocation.watchPosition(
        async (position) => {
          const now = Date.now();
          if (now - lastLocationSentRef.current < 15000) return;
          lastLocationSentRef.current = now;

          try {
            await shipmentServiceReal.updateLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              speed: position.coords.speed || undefined,
              heading: position.coords.heading || undefined,
              accuracy: position.coords.accuracy,
            });
            const locationData = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              speed: position.coords.speed,
              heading: position.coords.heading,
              timestamp: new Date().toISOString(),
              source: 'server',
            };
            localStorage.setItem('driverLocation', JSON.stringify(locationData));
            window.dispatchEvent(new CustomEvent('driverLocationUpdate', { detail: locationData }));
          } catch (error) {
            console.error('Failed to push background driver location:', error);
          }
        },
        (error) => {
          console.error('Background geolocation error:', error);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 5000,
          timeout: 10000,
        }
      );
    };

    void startWatch();

    return () => {
      if (Capacitor.isNativePlatform()) {
        void alwaysLocationService.stopBackgroundTrackingIfAvailable();
      }
      if (locationWatchRef.current !== null) {
        navigator.geolocation.clearWatch(locationWatchRef.current);
      }
    };
  }, [authUser]);

  useEffect(() => {
    const user = getStoredDriverMobileUser<any>() || authUser;
    if (!user?.id) return;

    let cancelled = false;
    const loadNotifications = async () => {
      try {
        const response: any = await notificationService.getNotifications({ userId: String(user.id), unreadOnly: true, compact: true, limit: 20 });
        if (cancelled) return;
        const items = Array.isArray(response?.data) ? response.data : [];
        if (!notificationCachePrimedRef.current) {
          primeNotificationAlertCache('driver-mobile', user.id, items);
          notificationCachePrimedRef.current = true;
        } else {
          announceNewNotifications('driver-mobile', user.id, items);
        }
      } catch (error) {
        console.error('Driver bottom nav notifications load failed:', error);
      }
    };

    void loadNotifications();
    const intervalId = window.setInterval(() => {
      void loadNotifications();
    }, 15000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [authUser]);

  const storedUser = getStoredDriverMobileUser<any>() || authUser;
  const isInternalDriver = storedUser?.role === 'internal-driver' || storedUser?.driverType === 'internal';

  const navItems = [
    {
      path: '/driver-mobile-home',
      icon: Home,
      label: 'Ana Sayfa',
    },
    {
      path: isInternalDriver ? '/driver-mobile-assignments' : '/driver-mobile-dashboard',
      icon: Package,
      label: 'Sevkiyatlar',
    },
    {
      path: '/driver-mobile-earnings',
      icon: Banknote,
      label: isInternalDriver ? 'Masraf/İzin' : 'Muhasebe',
    },
    {
      path: isInternalDriver ? '/driver-mobile-empty-return' : '/driver-mobile-documents',
      icon: isInternalDriver ? Route : FileText,
      label: isInternalDriver ? 'Boş Dönüş' : 'Belgeler',
    },
    {
      path: '/driver-mobile-support',
      icon: LifeBuoy,
      label: 'Destek',
    },
    {
      path: '/driver-mobile-profile',
      icon: User,
      label: 'Profil',
    },
  ];

  return createPortal(
    <>
    <CorporateQuickActions />
    <nav data-tour="bottom-nav"
      className="driver-bottom-nav bg-white border-t border-gray-200 shadow-lg"
    >
      <div className="grid min-h-[66px] grid-cols-6">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center justify-center gap-1 px-0.5 py-2 transition-colors ${
                  isActive
                    ? 'text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className={`h-[22px] w-[22px] ${isActive ? 'stroke-[2.5]' : ''}`} />
              <span className={`text-[11px] leading-none ${isActive ? 'font-bold' : 'font-medium'}`}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
    </>,
    document.body
  );
}
